import React, { ReactNode } from "react";
import Search from "./Search/Search";
import {
  Box,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Link,
  LinkProps,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { Eyebrow } from "./Eyebrow";
import { FiExternalLink } from "@react-icons/all-files/fi/FiExternalLink";
import { FiMenu } from "@react-icons/all-files/fi/FiMenu";
import { Link as GatsbyLink } from "gatsby";
import { useLocation } from "@gatsbyjs/reach-router";

export const NAV_HEIGHT_STYLES = "70px";
export const NAV_HEIGHT_EYEBROW = "120px";

const navLinks = [
  {
    title: "Learn",
    to: "/learn",
  },
  {
    title: "Tutorials",
    to: "/tutorials",
  },
  {
    title: "Community",
    to: "/community",
  },
  {
    title: "Events",
    to: "/events",
  },
];

export const activeAfterStyles = {
  content: '""',
  display: "inline-block",
  position: "relative",
  right: "-6px",
  top: 0,
  width: "8px",
  height: "8px",
  borderRadius: "8px",
  background: "purple.500",
};

type ActiveNavLinkProps = {
  children: ReactNode;
  to: string;
};

const navLinkColor = "gray.500";
const navLinkHover = {
  textDecoration: "none",
  color: "gray.900",
};

const NavLink = (props: LinkProps) => {
  return <Link color={navLinkColor} _hover={navLinkHover} {...props} />;
};

const ActiveNavLink = ({ to, children }: ActiveNavLinkProps) => {
  const location = useLocation();
  const isActive = location.pathname.includes(to);
  return (
    <Link
      fontSize="16px"
      as={GatsbyLink}
      color={isActive ? "gray.900" : navLinkColor}
      fontWeight={isActive ? "bold" : "normal"}
      _after={{
        ...(isActive ? activeAfterStyles : {}),
      }}
      _hover={navLinkHover}
      partiallyActive
      to={to}
    >
      {children}
    </Link>
  );
};

type NavItemsListProps = {
  items: Array<{
    title: string;
    to: string;
    isExternal?: boolean;
  }>;
};

const NavItemsList = ({ items }: NavItemsListProps) => {
  return (
    <>
      {items.map((link, index) =>
        link.isExternal ? (
          <NavLink
            fontSize="16px"
            alignItems="center"
            display="inline-flex"
            key={index}
            isExternal
            href={link.to}
            onClick={() => {
              window.gtag?.("event", "nav_link_click", {
                title: link.title,
              });
            }}
          >
            {link.title} &nbsp; <FiExternalLink />
          </NavLink>
        ) : (
          <ActiveNavLink
            key={index}
            to={link.to}
            onClick={() => {
              window.gtag?.("event", "nav_link_click", {
                title: link.title,
              });
            }}
          >
            {link.title}
          </ActiveNavLink>
        )
      )}
      <Search />
    </>
  );
};

export function Nav() {
  const bgColor = "white";
  const textColor = "gray.500";
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <Box
      bgColor={bgColor}
      borderWidth="0 0 1px 0"
      borderStyle="solid"
      borderBottomColor={"silver.400"}
      color={textColor}
      py="4"
      width="100%"
      position="fixed"
      top="0"
      zIndex={10}
      minHeight={NAV_HEIGHT_EYEBROW}
    >
      <Container maxW={{ base: "8xl", "2xl": "100%" }}>
        <VStack pb={{ base: "0", lg: "6" }}>
          <Eyebrow
            linkHref="https://summit.graphql.com/?utm_campaign=2025-10-06_Ev_Live_Summit&utm_medium=website&utm_source=graphql.com"
            linkText="Register for GraphQL Summit 2025 &#8594;"
            mobileText="Experience GraphQL Summit 2025: Register now &#8594;"
            text="Connect everything. Build anything. "
          />
        </VStack>
        <VStack pt="8">
          <Flex
            height="40px"
            width="100%"
            alignItems={"center"}
            justifyContent="space-between"
          >
            <Link
              as={GatsbyLink}
              color={textColor}
              _hover={{ textDecoration: "none", color: "brand.500" }}
              fontSize="1.2em"
              to="/"
            >
              <HStack>
                <Text color="blue.900">GraphQL.com</Text>
              </HStack>
            </Link>

            {/* desktop nav */}
            <HStack
              display={{ base: "none", lg: "flex" }}
              color={textColor}
              fontSize="0.9em"
              spacing={10}
            >
              <NavItemsList items={navLinks} />
            </HStack>

            {/* mobile nav */}
            <Box display={{ base: "block", lg: "none" }}>
              <FiMenu cursor="pointer" onClick={onOpen} />
            </Box>
            <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
              <DrawerOverlay />
              <DrawerContent>
                <DrawerCloseButton />
                <DrawerBody>
                  <VStack align="flex-start" spacing="4" mt="4">
                    <NavItemsList items={navLinks} />
                  </VStack>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </Flex>
        </VStack>
      </Container>
    </Box>
  );
}
